import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { calculateAuraStats, getAuraBadge, getAuraTier } from '../utils/aura';
import { submitVote, checkIfVoted, refreshAvatar } from '../utils/api';
import { getSession, spendTokens, syncTokensWithServer } from '../utils/session';

const CATEGORIES = [
  { id: 'drip_king', name: 'Drip King', emoji: '👑', icon: '/dripkingicon.png', type: 'good' },
  { id: 'drip_queen', name: 'Drip Queen', emoji: '👸', icon: '/dripqueenicon.png', type: 'good' },
  { id: 'bussin', name: "Bussin'", emoji: '✨', icon: '/bussinicon.png', type: 'good' },
  { id: 'fire_fit', name: 'Fire Fit', emoji: '🔥', icon: '/firefiticon.png', type: 'good' },
  { id: 'mid', name: 'Mid', emoji: '😐', icon: '/midicon.png', type: 'bad' },
  { id: 'ohio', name: 'Ohio', emoji: '🌽', icon: '/ohioicon.png', type: 'bad' },
  { id: 'cringe', name: 'Cringe', emoji: '😬', icon: '/cringeicon.png', type: 'bad' },
  { id: 'cooked', name: 'Cooked', emoji: '💀', icon: '/cookedicon.png', type: 'bad' },
];

function UserProfile({ onBack, initialUsername, onVoteComplete }) {
  const { user: loggedInUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState(initialUsername || '');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [voting, setVoting] = useState(false);
  const [voteMessage, setVoteMessage] = useState('');
  const [hasVoted, setHasVoted] = useState(false);
  const [categoryVoting, setCategoryVoting] = useState(false);
  const [categoryVoteMessage, setCategoryVoteMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');

  // Auto-search if initialUsername is provided
  useEffect(() => {
    if (initialUsername) {
      searchUser(initialUsername);
    }
  }, [initialUsername]);

  // Update meta tags when user profile is loaded
  useEffect(() => {
    if (user) {
      const auraPercent = user.auraStats?.auraPercent || 0;
      // Calculate net votes: good votes - bad votes
      const goodCategories = ['drip_king', 'drip_queen', 'bussin', 'fire_fit'];
      const badCategories = ['mid', 'ohio', 'cringe', 'cooked'];
      const goodVotes = goodCategories.reduce((sum, cat) => sum + (user.votes[cat] || 0), 0);
      const badVotes = badCategories.reduce((sum, cat) => sum + (user.votes[cat] || 0), 0);
      const netVotes = goodVotes - badVotes;

      const shareUrl = `${window.location.origin}/?share=${encodeURIComponent(user.username)}`;
      const imageUrl = `https://api.dripbattle.com/api/share-image/${encodeURIComponent(user.username)}`;

      // Update title
      document.title = `${user.username} - ${auraPercent}% Aura | Drip Battle`;

      // Update or create meta tags
      const updateMetaTag = (property, content) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateMetaTag('og:title', `${user.username} - ${auraPercent}% Aura | Drip Battle`);
      updateMetaTag('og:description', `Check out ${user.username}'s drip! ${netVotes} net votes, ${auraPercent}% aura. Drippy or chopped af?`);
      updateMetaTag('og:image', imageUrl);
      updateMetaTag('og:url', shareUrl);
      updateMetaTag('twitter:title', `${user.username} - ${auraPercent}% Aura | Drip Battle`);
      updateMetaTag('twitter:description', `Check out ${user.username}'s drip! ${netVotes} net votes, ${auraPercent}% aura. Drippy or chopped af?`);
      updateMetaTag('twitter:image', imageUrl);
      updateMetaTag('twitter:url', shareUrl);
    } else {
      // Reset to default meta tags
      document.title = 'Drip Battle - Roblox Avatar Voting';
    }
  }, [user]);

  const searchUser = async (username) => {
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    setLoading(true);
    setError('');
    setUser(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || '/api'}/user/${encodeURIComponent(username)}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          setError('User not found. Make sure they have submitted their avatar!');
        } else {
          setError('Failed to load user profile');
        }
        return;
      }

      const data = await response.json();
      setUser(data);

      // Check if user has already voted on this avatar
      const voteStatus = await checkIfVoted(data.id);
      setHasVoted(voteStatus.hasVoted);
    } catch (err) {
      console.error('Search error:', err);
      setError('An error occurred while searching');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    searchUser(searchQuery);
  };

  const handleRefreshAvatar = async () => {
    setRefreshing(true);
    setRefreshMessage('');

    try {
      const result = await refreshAvatar();

      if (result.success) {
        setRefreshMessage('✅ Avatar updated successfully!');
        // Update the displayed user with the new avatar
        setUser(result.avatar);

        // Clear message after 3 seconds
        setTimeout(() => {
          setRefreshMessage('');
        }, 3000);
      } else {
        setRefreshMessage('❌ ' + (result.error || 'Failed to update avatar'));
      }
    } catch (error) {
      console.error('Refresh avatar error:', error);
      setRefreshMessage('❌ Failed to update avatar');
    } finally {
      setRefreshing(false);
    }
  };

  const getTotalVotes = () => {
    if (!user) return 0;
    // Calculate net votes: good votes - bad votes
    const goodCategories = ['drip_king', 'drip_queen', 'bussin', 'fire_fit'];
    const badCategories = ['mid', 'ohio', 'cringe', 'cooked'];

    const goodVotes = goodCategories.reduce((sum, cat) => sum + (user.votes[cat] || 0), 0);
    const badVotes = badCategories.reduce((sum, cat) => sum + (user.votes[cat] || 0), 0);

    return goodVotes - badVotes;
  };

  const getTopCategory = () => {
    if (!user) return null;
    let maxVotes = 0;
    let topCat = null;

    Object.entries(user.votes).forEach(([catId, count]) => {
      if (count > maxVotes) {
        maxVotes = count;
        topCat = CATEGORIES.find(c => c.id === catId);
      }
    });

    return topCat;
  };

  const shareToX = () => {
    const auraStats = calculateAuraStats(
      user.auraStats?.fireSwipes || 0,
      user.auraStats?.leftSwipes || 0
    );
    const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);

    const text = `Check out ${user.username}'s Drip Battle profile! ${auraBadge.emoji}\n\n` +
      `${auraBadge.label} - ${auraStats.auraPercent}% Aura\n` +
      `${getTotalVotes()} total votes\n\n` +
      `Rate their drip at:`;
    // Add timestamp to bust social media cache
    const url = `${window.location.origin}/?share=${encodeURIComponent(user.username)}&v=${Date.now()}`;

    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const shareToFacebook = () => {
    // Add timestamp to bust social media cache
    const url = `${window.location.origin}/?share=${encodeURIComponent(user.username)}&v=${Date.now()}`;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const shareToDiscord = () => {
    const auraStats = calculateAuraStats(
      user.auraStats?.fireSwipes || 0,
      user.auraStats?.leftSwipes || 0
    );
    const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);

    // Add timestamp to bust social media cache
    const url = `${window.location.origin}/?share=${encodeURIComponent(user.username)}&v=${Date.now()}`;
    const message = `**${user.username}'s Drip Battle Profile** ${auraBadge.emoji}\n\n` +
      `${auraBadge.label} - ${auraStats.auraPercent}% Aura\n` +
      `${getTotalVotes()} total votes\n\n` +
      `${url}`;

    // Copy to clipboard for Discord paste
    navigator.clipboard.writeText(message);
    setShareMessage('✅ Copied to clipboard! Paste in Discord');
    setTimeout(() => setShareMessage(''), 3000);
  };

  const copyLink = () => {
    // Add timestamp to bust social media cache
    const url = `${window.location.origin}/?share=${encodeURIComponent(user.username)}&v=${Date.now()}`;
    navigator.clipboard.writeText(url);
    setShareMessage('✅ Link copied to clipboard!');
    setTimeout(() => setShareMessage(''), 3000);
  };

  const handleAuraVote = async (isFire) => {
    if (!user) return;

    console.log('Starting aura vote:', { userId: user.id, username: user.username, isFire });

    const session = getSession();
    const cost = 10; // Aura farming costs 10 Lit-Bits
    if (!session || session.tokens < cost) {
      setVoteMessage(`❌ You need ${cost} Lit-Bits to vote!`);
      setTimeout(() => setVoteMessage(''), 3000);
      return;
    }

    setVoting(true);

    try {
      console.log('Submitting vote to API...');
      // Submit vote - backend will deduct tokens
      const result = await submitVote(user.id, 'aura_farming', isFire);
      console.log('Vote result:', result);

      if (result.success) {
        setVoteMessage(isFire ? '🔥 Fire vote submitted!' : '💀 L vote submitted!');
        setHasVoted(true); // Mark as voted

        // Update balance immediately from vote response
        if (result.balance !== undefined) {
          const sessionData = getSession();
          if (sessionData) {
            sessionData.tokens = result.balance;
            localStorage.setItem('dripbattle_session', JSON.stringify(sessionData));
          }
          console.log('Balance updated to:', result.balance);

          // Immediately update parent component's live balance
          if (onVoteComplete) {
            await onVoteComplete(result.balance);
          }
        } else if (onVoteComplete) {
          // Fallback if no balance in response
          await onVoteComplete();
        }

        // Reload user data immediately to show updated stats
        console.log('Reloading user data...');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || '/api'}/user/${encodeURIComponent(user.username)}`
        );
        if (response.ok) {
          const data = await response.json();
          console.log('Updated user data:', data.auraStats);
          setUser(data);
        }

        // Clear message after showing success
        setTimeout(() => {
          setVoteMessage('');
        }, 2000);
      } else {
        console.error('Vote failed:', result);
        setVoteMessage(result.error || '❌ Failed to submit vote');
        setTimeout(() => setVoteMessage(''), 3000);
      }
    } catch (err) {
      console.error('Vote error:', err);
      setVoteMessage('❌ An error occurred');
      setTimeout(() => setVoteMessage(''), 3000);
    } finally {
      setVoting(false);
    }
  };

  const handleCategoryVote = async (categoryId, isPowerVote = false) => {
    if (!user) return;

    const cost = isPowerVote ? 12 : 5;
    const voteMultiplier = isPowerVote ? 3 : 1;

    console.log('Starting category vote:', { userId: user.id, username: user.username, categoryId, isPowerVote });

    const session = getSession();
    if (!session || session.tokens < cost) {
      setCategoryVoteMessage(`❌ You need ${cost} Lit-Bits to ${isPowerVote ? 'Power Vote' : 'vote'}!`);
      setTimeout(() => setCategoryVoteMessage(''), 3000);
      return;
    }

    setCategoryVoting(true);

    try {
      console.log('Submitting category vote to API...');
      // Submit vote - backend will deduct tokens
      const result = await submitVote(user.id, categoryId, true, voteMultiplier);
      console.log('Category vote result:', result);

      if (result.success) {
        const category = CATEGORIES.find(c => c.id === categoryId);
        const message = isPowerVote
          ? `💪 Power Vote! ${category.emoji} ${category.name} x3!`
          : `✅ Voted ${category.emoji} ${category.name}!`;
        setCategoryVoteMessage(message);

        // Update balance immediately from vote response
        if (result.balance !== undefined) {
          const sessionData = getSession();
          if (sessionData) {
            sessionData.tokens = result.balance;
            localStorage.setItem('dripbattle_session', JSON.stringify(sessionData));
          }
          console.log('Balance updated to:', result.balance);

          // Immediately update parent component's live balance
          if (onVoteComplete) {
            await onVoteComplete(result.balance);
          }
        } else if (onVoteComplete) {
          // Fallback if no balance in response
          await onVoteComplete();
        }

        // Reload user data immediately to show updated stats
        console.log('Reloading user data...');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || '/api'}/user/${encodeURIComponent(user.username)}`
        );
        if (response.ok) {
          const data = await response.json();
          console.log('Updated user data:', data.votes);
          setUser(data);
        }

        // Clear message after showing success
        setTimeout(() => {
          setCategoryVoteMessage('');
        }, 2000);
      } else {
        console.error('Category vote failed:', result);
        setCategoryVoteMessage(result.error || '❌ Failed to submit vote');
        setTimeout(() => setCategoryVoteMessage(''), 3000);
      }
    } catch (err) {
      console.error('Category vote error:', err);
      setCategoryVoteMessage('❌ An error occurred');
      setTimeout(() => setCategoryVoteMessage(''), 3000);
    } finally {
      setCategoryVoting(false);
    }
  };

  const shareToReddit = () => {
    const auraStats = calculateAuraStats(
      user.auraStats?.fireSwipes || 0,
      user.auraStats?.leftSwipes || 0
    );
    const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);

    const title = `${user.username}'s Drip Battle Profile - ${auraBadge.label} ${auraBadge.emoji}`;
    const url = `${window.location.origin}/?share=${encodeURIComponent(user.username)}`;

    window.open(
      `https://reddit.com/submit?title=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  // Calculate stats if user is loaded
  const auraStats = user ? calculateAuraStats(
    user.auraStats?.fireSwipes || 0,
    user.auraStats?.leftSwipes || 0
  ) : null;

  const auraBadge = auraStats ? getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes) : null;
  const topCategory = user ? getTopCategory() : null;

  return (
    <div className="user-profile-container">
      {/* Search Header */}
      <div className="profile-search-header">
        <button onClick={onBack} className="back-button">
          ← Back
        </button>
        <h1 className="profile-title">🔍 Search User Profile</h1>
      </div>

      {/* Search Bar */}
      <form className="profile-search-form" onSubmit={handleSearch}>
        <input
          type="text"
          className="profile-search-input"
          placeholder="Enter Roblox username..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="profile-search-button"
          disabled={loading}
        >
          {loading ? '🔄 Searching...' : '🔍 Search'}
        </button>
      </form>

      {error && (
        <div className="profile-error">
          <p>{error}</p>
        </div>
      )}

      {/* User Profile Display */}
      {user && (
        <div className="profile-display">
          {/* Main Profile Section */}
          <div className="profile-main">
            {/* Avatar Card - Center */}
            <div className="profile-center">
              <div className="profile-avatar-container">
                <img
                  src={user.avatarUrl}
                  alt={`${user.username}'s avatar`}
                  className="profile-avatar-image"
                />

                {/* Aura Badge - Top Right Corner */}
                {auraBadge && (
                  <div className="profile-aura-badge-corner">
                    <img src={auraBadge.emoji} alt={auraBadge.label} className="aura-badge-image-large" />
                  </div>
                )}
              </div>

              <h2 className="profile-username">
                {user.username}
                {user.isVerified && <span className="verified-badge" title="Verified Roblox Account">✓</span>}
              </h2>

              {/* Update Avatar Button - Only show for own profile */}
              {loggedInUser && loggedInUser.username === user.username && (
                <div className="update-avatar-section">
                  <button
                    onClick={handleRefreshAvatar}
                    disabled={refreshing}
                    className="update-avatar-btn"
                  >
                    {refreshing ? '🔄 Updating...' : '🔄 Update Avatar'}
                  </button>
                  {refreshMessage && (
                    <div className={`refresh-message ${refreshMessage.includes('✅') ? 'success' : 'error'}`}>
                      {refreshMessage}
                    </div>
                  )}
                  <p className="update-avatar-hint">Click to refresh your avatar from Roblox</p>
                </div>
              )}

              {/* Rate This Aura */}
              <div className="rate-aura-section">
                <h3 className="rate-title">Rate This Aura:</h3>
                {!hasVoted ? (
                  <>
                    <div className="rate-buttons">
                      <button
                        onClick={() => handleAuraVote(false)}
                        className="rate-button mid-rate"
                        disabled={voting}
                      >
                        <span className="rate-icon">💀</span>
                        <span className="rate-text">L Aura</span>
                      </button>
                      <button
                        onClick={() => handleAuraVote(true)}
                        className="rate-button fire-rate"
                        disabled={voting}
                      >
                        <span className="rate-icon">🔥</span>
                        <span className="rate-text">Fire Aura</span>
                      </button>
                    </div>
                    <p className="rate-cost">💰 10 Lit-Bits per vote</p>
                    {voteMessage && (
                      <div className={`vote-message ${voteMessage.includes('❌') ? 'error' : 'success'}`}>
                        {voteMessage}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="already-voted-message">
                    ✅ You've already rated this user's aura
                  </div>
                )}
              </div>

              {/* Vote on Categories */}
              <div className="vote-categories-section">
                <h3 className="vote-categories-title">Vote on Outfit:</h3>
                <p className="vote-category-cost">💰 5 Lit-Bits per vote • 12 Lit-Bits for Power Vote (3x)</p>

                {/* Good Vibes Categories */}
                <div className="vote-category-group">
                  <h4 className="vote-category-label good">🔥 Good Vibes</h4>
                  <div className="vote-category-buttons">
                    {CATEGORIES.filter(c => c.type === 'good').map(category => (
                      <div key={category.id} className="vote-category-item">
                        <div className="vote-category-header">
                          <span className="vote-category-emoji">
                            {category.icon ? (
                              <img src={category.icon} alt={category.name} className="category-icon" />
                            ) : (
                              category.emoji
                            )}
                          </span>
                          <span className="vote-category-name">{category.name}</span>
                        </div>
                        <div className="vote-category-actions">
                          <button
                            onClick={() => handleCategoryVote(category.id, false)}
                            className="vote-category-button-small good"
                            disabled={categoryVoting}
                          >
                            Vote (5 LB)
                          </button>
                          <button
                            onClick={() => handleCategoryVote(category.id, true)}
                            className="vote-category-button-small power good"
                            disabled={categoryVoting}
                          >
                            💪 Power (12 LB)
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bad Categories */}
                <div className="vote-category-group">
                  <h4 className="vote-category-label bad">💀 L Energy</h4>
                  <div className="vote-category-buttons">
                    {CATEGORIES.filter(c => c.type === 'bad').map(category => (
                      <div key={category.id} className="vote-category-item">
                        <div className="vote-category-header">
                          <span className="vote-category-emoji">
                            {category.icon ? (
                              <img src={category.icon} alt={category.name} className="category-icon" />
                            ) : (
                              category.emoji
                            )}
                          </span>
                          <span className="vote-category-name">{category.name}</span>
                        </div>
                        <div className="vote-category-actions">
                          <button
                            onClick={() => handleCategoryVote(category.id, false)}
                            className="vote-category-button-small bad"
                            disabled={categoryVoting}
                          >
                            Vote (5 LB)
                          </button>
                          <button
                            onClick={() => handleCategoryVote(category.id, true)}
                            className="vote-category-button-small power bad"
                            disabled={categoryVoting}
                          >
                            💪 Power (12 LB)
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {categoryVoteMessage && (
                  <div className={`vote-message ${categoryVoteMessage.includes('❌') ? 'error' : 'success'}`}>
                    {categoryVoteMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Stats - Right Side */}
            <div className="profile-stats-sidebar">
              {/* Aura Stats */}
              {auraStats && (
                <div className="stats-group aura-stats-group">
                  <h3 className="stats-group-title">⚡ Aura Stats</h3>
                  <div className="stats-grid-compact">
                    <div className="stat-box">
                      <div className="stat-icon">🔥</div>
                      <div className="stat-value">{auraStats.fireSwipes}</div>
                      <div className="stat-label">Fire</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-icon">💀</div>
                      <div className="stat-value">{auraStats.leftSwipes}</div>
                      <div className="stat-label">L's</div>
                    </div>
                    <div className="stat-box highlight">
                      <div className="stat-icon">⚡</div>
                      <div className="stat-value">{auraStats.auraPercent}%</div>
                      <div className="stat-label">Aura</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-icon">📊</div>
                      <div className="stat-value">{auraStats.netAura}</div>
                      <div className="stat-label">Net</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Overall Stats */}
              <div className="stats-group overall-stats-group">
                <h3 className="stats-group-title">📊 Overall Stats</h3>
                <div className="stats-grid-compact">
                  <div className="stat-box">
                    <div className="stat-icon">🏆</div>
                    <div className="stat-value">{getTotalVotes()}</div>
                    <div className="stat-label">Net Votes</div>
                  </div>
                  {topCategory && (
                    <div className="stat-box highlight">
                      <div className="stat-icon">
                        {topCategory.icon ? (
                          <img src={topCategory.icon} alt={topCategory.name} className="category-icon" />
                        ) : (
                          topCategory.emoji
                        )}
                      </div>
                      <div className="stat-value">{user.votes[topCategory.id]}</div>
                      <div className="stat-label">{topCategory.name}</div>
                    </div>
                  )}
                  <div className="stat-box">
                    <div className="stat-icon">👥</div>
                    <div className="stat-value">{auraStats.totalSwipes}</div>
                    <div className="stat-label">Swipes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Share Section - Below Main */}
          <div className="profile-share-section">
            <h3 className="share-title">📤 Share Profile</h3>
            <div className="share-buttons">
              <button onClick={shareToX} className="share-button twitter">
                <span className="share-icon">𝕏</span>
                <span className="share-text">X</span>
              </button>
              <button onClick={shareToFacebook} className="share-button facebook">
                <span className="share-icon">📘</span>
                <span className="share-text">Facebook</span>
              </button>
              <button onClick={shareToDiscord} className="share-button discord">
                <span className="share-icon">💬</span>
                <span className="share-text">Discord</span>
              </button>
              <button onClick={shareToReddit} className="share-button reddit">
                <span className="share-icon">🤖</span>
                <span className="share-text">Reddit</span>
              </button>
              <button onClick={copyLink} className="share-button copy">
                <span className="share-icon">🔗</span>
                <span className="share-text">Copy</span>
              </button>
            </div>
            {shareMessage && (
              <div className="share-message">{shareMessage}</div>
            )}
          </div>

          {/* Category Breakdown - Full Width Below */}
          <div className="profile-categories-section">

            <div className="stats-group category-breakdown">
              <h3 className="stats-group-title">📈 Category Votes</h3>

              <div className="category-votes-section">
                <h4 className="category-section-label good">🔥 Good Vibes</h4>
                <div className="category-votes-grid">
                  {CATEGORIES.filter(c => c.type === 'good').map(category => (
                    <div key={category.id} className="category-vote-item good">
                      <span className="category-vote-emoji">
                        {category.icon ? (
                          <img src={category.icon} alt={category.name} className="category-icon" />
                        ) : (
                          category.emoji
                        )}
                      </span>
                      <span className="category-vote-name">{category.name}</span>
                      <span className="category-vote-count">{user.votes[category.id] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="category-votes-section">
                <h4 className="category-section-label bad">💀 L Energy</h4>
                <div className="category-votes-grid">
                  {CATEGORIES.filter(c => c.type === 'bad').map(category => (
                    <div key={category.id} className="category-vote-item bad">
                      <span className="category-vote-emoji">
                        {category.icon ? (
                          <img src={category.icon} alt={category.name} className="category-icon" />
                        ) : (
                          category.emoji
                        )}
                      </span>
                      <span className="category-vote-name">{category.name}</span>
                      <span className="category-vote-count">{user.votes[category.id] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submission Date */}
              <div className="profile-meta">
                <p className="meta-text">
                  📅 Submitted: {new Date(user.submittedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!user && !loading && !error && (
        <div className="profile-empty-state">
          <div className="empty-state-icon">🔍</div>
          <h2>Search for a User</h2>
          <p>Enter a Roblox username to view their Drip Battle stats and badges</p>
        </div>
      )}
    </div>
  );
}

export default UserProfile;
